import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface ConfirmByTypingDialogProps {
  /** The element that opens the dialog (rendered as the trigger). */
  children: ReactNode;
  title: string;
  description?: ReactNode;
  /** The exact value the user must type — compared trimmed and case-insensitively — to confirm. */
  expectedValue: string;
  inputLabel: string;
  placeholder?: string;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  /** Disables the confirm action while the triggered work is in flight. */
  pending?: boolean;
  onConfirm: () => void;
}

/**
 * A confirmation prompt that only enables its action once the user types an expected value
 * (e.g. their email). For deliberate, irreversible actions where a single click is too easy.
 */
export function ConfirmByTypingDialog({
  children,
  title,
  description,
  expectedValue,
  inputLabel,
  placeholder,
  confirmLabel,
  cancelLabel,
  destructive = false,
  pending = false,
  onConfirm,
}: ConfirmByTypingDialogProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const inputId = useId();
  const matches = value.trim().toLowerCase() === expectedValue.trim().toLowerCase();
  const canConfirm = matches && !pending;

  function onOpenChange(next: boolean) {
    setOpen(next);
    if (!next) setValue('');
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description ? <AlertDialogDescription>{description}</AlertDialogDescription> : null}
        </AlertDialogHeader>
        <div className="space-y-2">
          <Label htmlFor={inputId}>{inputLabel}</Label>
          <Input
            id={inputId}
            value={value}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            placeholder={placeholder}
            onChange={(e) => setValue(e.target.value)}
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>{cancelLabel ?? t('common.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            variant={destructive ? 'destructive' : 'default'}
            disabled={!canConfirm}
            onClick={(e) => {
              if (!canConfirm) {
                e.preventDefault();
                return;
              }
              onConfirm();
            }}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
